import { useQuery } from '@tanstack/react-query';
import { Accordion } from '@tempo-io/sui-accordion';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/common/api/items';
import { isItemDependencyBlocked } from '@/common/api/types/dashboard/health';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import type { Item } from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { associateById } from '@/common/utils/associateById';
import { INSIGHTS_STALE_TIME } from '@/features/insights-dashboard/components/data/constants';
import type { DependencyTabAdapter } from '@/features/item-panel/components/adapters/types';
import { useTabAdapter } from '@/features/item-panel/components/context/TabAdapterContext';
import { useDependencyForecastDates } from '@/features/item-panel/components/hooks/useDependencyForecastDates';
import { AddDependencyControl } from '@/features/item-panel/components/tabs/dependencies/AddDependencyControl';
import { DependencySection } from '@/features/item-panel/components/tabs/dependencies/DependencySection';
import {
  buildColumns,
  buildGroupingColDef,
} from '@/features/item-panel/components/tabs/dependencies/lib/columns';
import {
  DEFAULT_OPEN_SECTIONS,
  DEPENDENCY_DROPDOWN_ITEM_LIMIT,
} from '@/features/item-panel/components/tabs/dependencies/lib/constants';
import type { DependencySectionValue } from '@/features/item-panel/components/tabs/dependencies/lib/types';
import { useDependencyMutationFlows } from '@/features/item-panel/components/tabs/dependencies/lib/useDependencyMutationFlows';
import { useItemHealthByItemId } from '@/features/item-panel/components/tabs/dependencies/lib/useItemHealthByItemId';
import {
  blockedRowClassName,
  sortByDate,
  withSchedules,
} from '@/features/item-panel/components/tabs/dependencies/lib/utils';
import { useItemDependenciesPanelQuery } from '@/features/item-panel/queries/useItemDependenciesPanelQuery';
import { LoopPlannerScenarioItemsApi } from '@/features/planner/api/scenarioItems';
import { plannerQueryKeys } from '@/features/planner/queryKeys';

interface DependenciesProps {
  item: Item;
}

export const Dependencies = ({ item }: DependenciesProps) => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<DependencyTabAdapter>();
  const {
    itemStatuses,
    itemTypes,
    predecessors,
    successors,
    predecessorInheritedIds,
    successorInheritedIds,
    isPredecessorsLoading,
    isSuccessorsLoading,
  } = useItemDependenciesPanelQuery(item);
  const predecessorIds = useMemo(() => predecessors.map((p) => p.id), [predecessors]);
  const successorIds = useMemo(() => successors.map((s) => s.id), [successors]);
  const allDependencyIds = useMemo(
    () => [...predecessorIds, ...successorIds],
    [predecessorIds, successorIds],
  );
  const forecastDates = useDependencyForecastDates(allDependencyIds, adapter.scenarioContext);
  const healthByItemId = useItemHealthByItemId(allDependencyIds);
  const blockedIds = useMemo(
    () =>
      new Set(
        allDependencyIds.filter((id) => {
          const health = healthByItemId.get(id);
          return health ? isItemDependencyBlocked(health) : false;
        }),
      ),
    [allDependencyIds, healthByItemId],
  );
  const currentItemHealth = useItemHealthByItemId([item.id]).get(item.id);
  const isCurrentItemBlocked = currentItemHealth
    ? isItemDependencyBlocked(currentItemHealth)
    : false;

  const itemTypesById = useMemo<ItemTypeMap>(() => associateById(itemTypes), [itemTypes]);
  const excludedItemIds = useMemo(
    () => [item.id, ...predecessorIds, ...successorIds],
    [item.id, predecessorIds, successorIds],
  );
  const canAdd = adapter.canAddDependency() && adapter.loopId != null;
  const canRemove = adapter.isEditable();
  const scenarioContext = adapter.scenarioContext;
  const pickerFilter = useMemo<ItemFiltersType>(
    () =>
      scenarioContext == null && adapter.loopId != null
        ? { loopId: { value: [adapter.loopId], operator: 'eq' } }
        : {},
    [scenarioContext, adapter.loopId],
  );

  const { data: candidateCount } = useQuery({
    queryKey: scenarioContext
      ? [...plannerQueryKeys.scenarioItems.root(scenarioContext), 'search-count']
      : commonQueryKeys.items.byFilters({
          filter: pickerFilter,
          limit: 1,
          hierarchical: false,
        }),
    queryFn: async () => {
      const response = scenarioContext
        ? await LoopPlannerScenarioItemsApi.getScenarioItems({ ...scenarioContext, limit: 1 })
        : await ItemsApi.getItems({ filter: pickerFilter, limit: 1, hierarchical: false });
      return response.recordCount;
    },
    enabled: canAdd,
    staleTime: INSIGHTS_STALE_TIME,
  });
  const showEagerDropdown =
    candidateCount != null &&
    candidateCount - excludedItemIds.length <= DEPENDENCY_DROPDOWN_ITEM_LIMIT;

  const { addFlow, removeFlow, openAdd, cancelAdd, add, remove } = useDependencyMutationFlows(
    adapter,
    item.id,
  );

  // Memoized so identity stays stable across incidental re-renders (e.g. health
  // polling) — otherwise a fresh rows array on every render resets the grid's
  // client-side sort state right after the user picks Asc/Desc from the column menu.
  const predecessorRows = useMemo(
    () => sortByDate(withSchedules(predecessors, forecastDates), 'forecastedFinishDateTime', -1),
    [predecessors, forecastDates],
  );
  const successorRows = useMemo(
    () => sortByDate(withSchedules(successors, forecastDates), 'forecastedStartDateTime', 1),
    [successors, forecastDates],
  );

  const isRemoveInFlight = removeFlow.kind === 'submitting';

  const sections = [
    {
      value: 'predecessors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessors),
      description: t(
        ($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessorsDescription,
      ),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addPredecessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOn),
      rows: predecessorRows,
      inheritedIds: predecessorInheritedIds,
      isLoading: isPredecessorsLoading,
    },
    {
      value: 'successors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successors),
      description: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successorsDescription),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addSuccessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnables),
      rows: successorRows,
      inheritedIds: successorInheritedIds,
      isLoading: isSuccessorsLoading,
    },
  ];

  const renderAddControl = (section: DependencySectionValue, addLabel: string): React.ReactNode => {
    if (!canAdd) {
      return null;
    }
    const isAdding = addFlow.kind !== 'idle' && addFlow.section === section;
    return (
      <AddDependencyControl
        addLabel={addLabel}
        pickerFilter={pickerFilter}
        excludedItemIds={excludedItemIds}
        itemTypesById={itemTypesById}
        isAdding={isAdding}
        isSubmitting={isAdding && addFlow.kind === 'submitting'}
        errorMessage={isAdding && addFlow.kind === 'error' ? addFlow.message : null}
        eagerLoad={showEagerDropdown}
        scenarioLoopId={scenarioContext?.loopId}
        scenarioId={scenarioContext?.scenarioId}
        onOpen={() => openAdd(section)}
        onCancel={cancelAdd}
        onSelect={(pickedItemId) => {
          void add(section, pickedItemId);
        }}
      />
    );
  };

  const blockedBadgeLabelFor = (section: (typeof sections)[number]): string | null => {
    if (section.value === 'predecessors') {
      return isCurrentItemBlocked ? t(($) => $.insights.dependency.blocked) : null;
    }
    const blockingCount = section.rows.filter((row) => blockedIds.has(row.id)).length;
    return blockingCount > 0
      ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blocking, {
          count: blockingCount,
        })
      : null;
  };

  return (
    <div className="flex flex-col gap-4">
      <Accordion
        type="multiple"
        defaultValue={DEFAULT_OPEN_SECTIONS}
        className="flex flex-col gap-4"
      >
        {sections.map((section) => (
          <DependencySection
            key={section.value}
            value={section.value}
            title={section.title}
            description={section.description}
            blockedBadgeLabel={blockedBadgeLabelFor(section)}
            groupingColDef={buildGroupingColDef(section.nameHeader, itemTypes)}
            columns={buildColumns({
              section: section.value,
              currentItemId: item.id,
              currentItemHealth,
              healthByItemId,
              itemStatuses,
              canRemove,
              isRemoveInFlight,
              inheritedItemIds: section.inheritedIds,
              onRemove: (removeSection, rowItemId) => {
                void remove(removeSection, rowItemId);
              },
              t,
            })}
            rows={section.rows}
            isLoading={section.isLoading}
            rowClassName={blockedRowClassName(blockedIds)}
            removeError={
              removeFlow.kind === 'error' && removeFlow.section === section.value
                ? removeFlow.message
                : null
            }
            addControl={renderAddControl(section.value, section.addLabel)}
          />
        ))}
      </Accordion>
    </div>
  );
};
